import React, { useEffect, useState } from 'react';
import { Button } from '@scalingworks/react-admin-ui';
import { useTranslate } from '@refinedev/core';
import { Loading } from '~/components';
import { formatCurrency } from '~/resources/helpers';
import { WalletBalanceAdjustmentModal } from './WalletBalanceAdjustmentModal';
import { TransactionModal } from './TransactionModal';
import {
  useGetCustomerWalletBalanceQuery,
  useGetCustomerWalletTransactionsQuery,
} from '~/generated/hooks';
import { FiEdit3, FiList } from 'react-icons/fi';
import { IoWalletOutline } from 'react-icons/io5';

interface WalletDashboardProps {
  customerId: string;
  loading?: boolean;
}

export const WalletDashboard: React.FC<WalletDashboardProps & { fetchIndex?: number }> = ({
  customerId,
  loading: parentLoading,
  fetchIndex,
}) => {
  const t = useTranslate();
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  const {
    data: balanceData,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useGetCustomerWalletBalanceQuery(
    { customerIdOrPosId: customerId },
    { enabled: !!customerId }
  );

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    refetch: refetchTransactions,
  } = useGetCustomerWalletTransactionsQuery(
    { customerIdOrPosId: customerId },
    { enabled: !!customerId }
  );

  const refetchAll = () => {
    refetchBalance();
    refetchTransactions();
  };

  useEffect(() => {
    refetchAll();
  }, [fetchIndex]);

  const balance = balanceData?.customerWalletBalance?.balance || 0;
  const currency = balanceData?.customerWalletBalance?.currency || 'MYR';
  const transactions = transactionsData?.customerWalletTransactions?.items || [];

  const isLoading = parentLoading || balanceLoading || transactionsLoading;

  if (isLoading) return <Loading />;
  if (!balanceData?.customerWalletBalance) return null;

  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <IoWalletOutline className="text-gray-600" size={20} />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Customer Wallet</h3>
      </div>

      {/* Balance and Buttons Row */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-2">Current Balance</p>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(balance, currency)}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="solid"
            size="sm"
            onClick={() => setShowAdjustModal(true)}
            className="flex items-center gap-2"
          >
            <FiEdit3 size={16} />
            Adjust
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTransactionModal(true)}
            className="flex items-center gap-2"
          >
            <FiList size={16} />
            Transactions
          </Button>
        </div>
      </div>

      {/* Modals */}
      <WalletBalanceAdjustmentModal
        visible={showAdjustModal}
        onOpenChange={setShowAdjustModal}
        customerId={customerId}
        currentBalance={balance}
        currency={currency}
        onSuccess={refetchAll}
      />

      <TransactionModal
        visible={showTransactionModal}
        onOpenChange={setShowTransactionModal}
        transactions={transactions}
        currency={currency}
      />
    </div>
  );
};
